import {
  GroupShapeIcon,
  type GroupShape,
} from "@/components/shapes/GroupShapeIcon";
import { groupColors } from "@/configs/map/groups";
import { useActiveDataflowId } from "@/stores/dataflow";
import { useMapStore } from "@/stores/map";
import { logger } from "@/utils/debug";
import {
  Button,
  Checkbox,
  ColorInput,
  Group,
  Modal,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";

export function WorkspaceModalMapCluster({
  onClose,
  title = "Configure Cluster",
  chunkValue,
  initialDisplayName,
  initialFrom,
  initialTo,
  initialColor,
  initialShape,
  initialVisible,
  isLayerRegion,
}: {
  onClose: () => void;
  title?: string;
  chunkValue?: string;
  initialDisplayName?: string;
  initialFrom?: string;
  initialTo?: string;
  initialColor?: string;
  initialShape?: GroupShape;
  initialVisible?: boolean;
  isLayerRegion?: boolean;
}) {
  const activeDataflowId = useActiveDataflowId();
  const updateChunkAndPoints = useMapStore(
    (state) => state.updateChunkAndPoints
  );
  const setLayerRegionConfig = useMapStore(
    (state) => state.setLayerRegionConfig
  );
  const getChunkConfig = useMapStore((state) => state.getChunkConfig);
  const getLayerRegionConfig = useMapStore((state) => state.getLayerRegionConfig);
  const getChunks = useMapStore((state) => state.getChunks);
  const setVisibleGroups = useMapStore((state) => state.setVisibleGroups);
  const visibleGroups = useMapStore(
    (state) => state.groups[activeDataflowId || ""]?.visibleGroups || []
  );

  // Get chunk to extract from/to values if not provided
  const chunk =
    activeDataflowId && chunkValue
      ? getChunks(activeDataflowId)?.find((c) => c.value === chunkValue)
      : null;

  const currentConfig =
    activeDataflowId && chunkValue
      ? getChunkConfig(activeDataflowId, chunkValue)
      : null;

  // Extract from/to from chunk label if not provided
  const extractFromTo = (label: string) => {
    const match = label.match(/^(.+?)\s*-\s*(.+)$/);
    if (match) {
      return { from: match[1], to: match[2] };
    }
    return { from: label, to: label };
  };

  const defaultFromTo = chunk?.label
    ? extractFromTo(chunk.label)
    : { from: "", to: "" };

  // Get layer region config if this is a layer region
  const layerRegionConfig = isLayerRegion && activeDataflowId && chunkValue
    ? getLayerRegionConfig(activeDataflowId, chunkValue)
    : null;

  const form = useForm<{
    displayName: string;
    from: string;
    to: string;
    color: string;
    shape: GroupShape;
    visible: boolean;
  }>({
    initialValues: {
      displayName:
        initialDisplayName ||
        (isLayerRegion ? layerRegionConfig?.displayName : undefined) ||
        currentConfig?.displayName ||
        chunk?.displayName ||
        chunk?.label ||
        "",
      from: initialFrom || currentConfig?.from || defaultFromTo.from,
      to: initialTo || currentConfig?.to || defaultFromTo.to,
      color: 
        initialColor || 
        (isLayerRegion ? layerRegionConfig?.color : undefined) ||
        currentConfig?.color || 
        chunk?.color || 
        "#003399",
      shape: initialShape || currentConfig?.shape || "circle",
      visible:
        initialVisible !== undefined
          ? initialVisible
          : isLayerRegion
          ? (layerRegionConfig?.isVisible ?? true)
          : chunk
          ? chunk.values.every((v) => visibleGroups.includes(v))
          : true,
    },
    validate: {
      displayName: (value) =>
        !value || value.trim() === "" ? "Display name is required" : null,
      from: (value) => (!isLayerRegion && !value ? "From value is required" : null),
      to: (value) => (!isLayerRegion && !value ? "To value is required" : null),
      color: (value) => (!value ? "Color is required" : null),
    },
  });

  const handleSubmit = (values: {
    displayName: string;
    from: string;
    to: string;
    color: string;
    shape: GroupShape;
    visible: boolean;
  }) => {
    if (!activeDataflowId || !chunkValue) {
      logger.error(
        "Cannot save cluster config: missing activeDataflowId or chunkValue",
        {
          activeDataflowId,
          chunkValue,
        }
      );
      return;
    }

    if (isLayerRegion) {
      // Update layer region config
      setLayerRegionConfig(activeDataflowId, chunkValue, {
        displayName: values.displayName.trim(),
        color: values.color,
        isVisible: values.visible,
      });
    } else {
      // Update chunk config
      updateChunkAndPoints(activeDataflowId, chunkValue, {
        displayName: values.displayName.trim(),
        from: values.from.trim(),
        to: values.to.trim(),
        color: values.color,
        shape: values.shape,
      });

      // Update visibility for all points in chunk
      const chunk = getChunks(activeDataflowId)?.find(
        (c) => c.value === chunkValue
      );
      if (chunk) {
        const currentVisible = new Set<string>(visibleGroups);
        if (values.visible) {
          // Add all chunk values to visible groups
          chunk.values.forEach((value) => {
            currentVisible.add(value);
          });
        } else {
          // Remove all chunk values from visible groups
          chunk.values.forEach((value) => {
            currentVisible.delete(value);
          });
        }
        setVisibleGroups(activeDataflowId, Array.from(currentVisible));
      }
    }

    onClose();
  };

  return (
    <Modal
      opened={true}
      onClose={onClose}
      title={title}
      size="md"
      closeOnClickOutside={false}
      centered
    >
      <form
        onSubmit={form.onSubmit(handleSubmit, (errors) => {
          logger.error("Form validation errors:", errors);
        })}
      >
        <Stack gap="md">
          <TextInput
            label="Display Name"
            placeholder="Enter display name"
            {...form.getInputProps("displayName")}
            required
            error={form.errors.displayName}
          />

          {!isLayerRegion && (
            <Group grow>
              <TextInput
                label="From Value"
                placeholder="From"
                {...form.getInputProps("from")}
                required
                error={form.errors.from}
              />
              <TextInput
                label="To Value"
                placeholder="To"
                {...form.getInputProps("to")}
                required
                error={form.errors.to}
              />
            </Group>
          )}

          <ColorInput
            label={isLayerRegion ? "Region Color" : "Cluster Color"}
            format="hex"
            swatches={groupColors}
            {...form.getInputProps("color")}
            required
            error={form.errors.color}
          />

          {!isLayerRegion && (
            <div>
              <Text size="sm" fw={500} mb="xs">
                Shape
              </Text>
              <SegmentedControl
                fullWidth
                data={[
                {
                  value: "circle",
                  label: (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                      }}
                    >
                      <GroupShapeIcon
                        shape="circle"
                        color={form.values.color}
                        size={20}
                      />
                    </div>
                  ),
                },
                {
                  value: "square",
                  label: (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                      }}
                    >
                      <GroupShapeIcon
                        shape="square"
                        color={form.values.color}
                        size={20}
                      />
                    </div>
                  ),
                },
                {
                  value: "triangle",
                  label: (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                      }}
                    >
                      <GroupShapeIcon
                        shape="triangle"
                        color={form.values.color}
                        size={20}
                      />
                    </div>
                  ),
                },
                {
                  value: "star",
                  label: (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                      }}
                    >
                      <GroupShapeIcon
                        shape="star"
                        color={form.values.color}
                        size={20}
                      />
                    </div>
                  ),
                },
                {
                  value: "diamond",
                  label: (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                      }}
                    >
                      <GroupShapeIcon
                        shape="diamond"
                        color={form.values.color}
                        size={20}
                      />
                    </div>
                  ),
                },
              ]}
              {...form.getInputProps("shape")}
            />
            </div>
          )}

          <Checkbox
            label="Visible"
            {...form.getInputProps("visible", { type: "checkbox" })}
          />

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">Save</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
